import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { apiUrl } from "../lib/apiBase";

function getToken() {
  return (
    localStorage.getItem("ga_token") ||
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    ""
  );
}

async function request(path, options = {}) {
  const response = await fetch(apiUrl(path), {
    method: options.method || "GET",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: `Bearer ${getToken()}`,
    },
    body: options.body ? JSON.stringify(options.body) : undefined,
    cache: "no-store",
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data?.message || data?.error || `Request failed (${response.status})`);
    error.status = response.status;
    error.data = data;
    throw error;
  }
  return data;
}

function money(cents) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
    Number(cents || 0) / 100
  );
}

function localInput(value) {
  if (!value) return "";
  const date = new Date(value);
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

function defaultStart() {
  const date = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
  date.setMinutes(0, 0, 0);
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

const EMPTY_FORM = {
  id: "",
  title: "GAStreaming Digital Venue Demo",
  slug: "digital-venue-demo",
  subtitle: "One exclusive performance. Live from anywhere.",
  description: "A private mock event used to prove ticketing, secure access, production, and revenue accounting before any entertainer is approached.",
  performerName: "",
  artworkUrl: "",
  startsAt: defaultStart(),
  durationMinutes: 90,
  doorsOpenMinutes: 30,
  ticketPriceCents: 1500,
  capacity: 1000,
  performerBps: 8000,
  status: "draft",
  ticketSalesOpen: false,
  testPlaybackEnabled: false,
  allowReplay: false,
  replayEndsAt: "",
  chatEnabled: false,
  refundPolicy: "Refunds are available if the event is cancelled. Other refund terms will be finalized before public ticket sales.",
  livePlaybackId: "",
  replayPlaybackId: "",
  playbackRestrictionId: "",
};

function formFromEvent(event) {
  if (!event) return { ...EMPTY_FORM, startsAt: defaultStart() };
  return {
    id: event.id,
    title: event.title || "",
    slug: event.slug || "",
    subtitle: event.subtitle || "",
    description: event.description || "",
    performerName: event.performerName || "",
    artworkUrl: event.artworkUrl || "",
    startsAt: localInput(event.startsAt),
    durationMinutes: event.durationMinutes || 90,
    doorsOpenMinutes: event.doorsOpenMinutes || 30,
    ticketPriceCents: event.ticketPriceCents || 1500,
    capacity: event.capacity || 1000,
    performerBps: event?.revenueSplit?.performerBps || 8000,
    status: event.status || "draft",
    ticketSalesOpen: !!event.ticketSalesOpen,
    testPlaybackEnabled: !!event.testPlaybackEnabled,
    allowReplay: !!event.allowReplay,
    replayEndsAt: localInput(event.replayEndsAt),
    chatEnabled: !!event.chatEnabled,
    refundPolicy: event.refundPolicy || "",
    livePlaybackId: event?.stream?.livePlaybackId || "",
    replayPlaybackId: event?.stream?.replayPlaybackId || "",
    playbackRestrictionId: event?.stream?.playbackRestrictionId || "",
  };
}

export default function DigitalVenueAdmin() {
  const [events, setEvents] = useState([]);
  const [readiness, setReadiness] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [production, setProduction] = useState(null);
  const [advanced, setAdvanced] = useState(false);

  const current = useMemo(
    () => events.find((event) => event.id === form.id) || null,
    [events, form.id]
  );

  const load = useCallback(async (keepId = "") => {
    setLoading(true);
    setError("");
    try {
      const data = await request("/digital-venue/admin/events");
      setEvents(data.events || []);
      setReadiness(data.readiness || null);
      const selected = (data.events || []).find((event) => event.id === keepId) || data.events?.[0];
      setForm(formFromEvent(selected));
    } catch (err) {
      setError(err.message || "Could not load the digital venue control center.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  function update(key, value) {
    setForm((currentForm) => ({ ...currentForm, [key]: value }));
  }

  async function save(overrides = {}) {
    setBusy("save");
    setError("");
    setNotice("");
    try {
      const next = { ...form, ...overrides };
      const payload = {
        id: next.id || undefined,
        title: next.title,
        slug: next.slug,
        subtitle: next.subtitle,
        description: next.description,
        performerName: next.performerName,
        artworkUrl: next.artworkUrl,
        startsAt: new Date(next.startsAt).toISOString(),
        durationMinutes: Number(next.durationMinutes),
        doorsOpenMinutes: Number(next.doorsOpenMinutes),
        ticketPriceCents: Number(next.ticketPriceCents),
        capacity: Number(next.capacity),
        performerBps: Number(next.performerBps),
        status: next.status,
        ticketSalesOpen: Boolean(next.ticketSalesOpen),
        testPlaybackEnabled: Boolean(next.testPlaybackEnabled),
        allowReplay: Boolean(next.allowReplay),
        replayEndsAt: next.replayEndsAt ? new Date(next.replayEndsAt).toISOString() : null,
        chatEnabled: Boolean(next.chatEnabled),
        refundPolicy: next.refundPolicy,
        stream: {
          livePlaybackId: next.livePlaybackId,
          livePlaybackPolicy: "signed",
          replayPlaybackId: next.replayPlaybackId,
          replayPlaybackPolicy: "signed",
          playbackRestrictionId: next.playbackRestrictionId,
        },
      };
      const data = await request("/digital-venue/admin/events", { method: "POST", body: payload });
      setNotice("Private event configuration saved. Nothing was published or sent to an entertainer.");
      setReadiness(data.readiness || readiness);
      await load(data.event.id);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy("");
    }
  }

  async function provisionMux() {
    if (!form.id) {
      setError("Save the private event before creating its Mux venue.");
      return;
    }
    if (!window.confirm("Create a real private signed Mux live stream for this mock event? This creates a Mux resource, but it does not publish or start the event.")) return;
    setBusy("mux");
    setError("");
    try {
      const data = await request(`/digital-venue/admin/events/${encodeURIComponent(form.id)}/provision-mux`, { method: "POST" });
      setProduction(data.production || null);
      setNotice("Private Mux venue created. Copy the production credentials now; the stream key is intentionally shown only in this response.");
      await load(form.id);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy("");
    }
  }

  async function copy(value, label) {
    try {
      await navigator.clipboard.writeText(value);
      setNotice(`${label} copied.`);
    } catch {
      window.prompt(`Copy ${label.toLowerCase()}:`, value);
    }
  }

  const previewUrl = current
    ? `${window.location.origin}/events/${current.slug}?preview=${encodeURIComponent(current.previewCode)}`
    : "";
  const revenue = current?.revenue || {};

  return (
    <main className="dva-page">
      <style>{`
        .dva-page{max-width:1280px;margin:0 auto;padding:20px 16px 84px;color:#fff}.dva-hero,.dva-card{border:1px solid rgba(255,65,77,.28);background:linear-gradient(145deg,rgba(55,7,12,.92),rgba(7,7,9,.97));box-shadow:0 24px 70px rgba(0,0,0,.42)}.dva-hero{border-radius:28px;padding:clamp(24px,5vw,52px)}.dva-card{border-radius:20px;padding:20px}.dva-eyebrow{font-size:12px;font-weight:1000;letter-spacing:.17em;color:#ffabb2}.dva-title{font-size:clamp(2.5rem,7vw,5.8rem);line-height:.9;letter-spacing:-.055em;margin:10px 0}.dva-copy{color:rgba(255,255,255,.72);line-height:1.65;max-width:900px}.dva-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(280px,1fr));gap:14px;margin-top:18px}.dva-layout{display:grid;grid-template-columns:280px minmax(0,1fr);gap:18px;margin-top:18px}.dva-list{display:grid;gap:10px}.dva-event-btn{width:100%;text-align:left;border:1px solid rgba(255,255,255,.12);background:rgba(255,255,255,.045);color:#fff;border-radius:14px;padding:14px;cursor:pointer}.dva-event-btn--active{border-color:#ff5e6c;background:rgba(142,10,23,.38)}.dva-form{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:13px}.dva-field{display:grid;gap:7px}.dva-field--wide{grid-column:1/-1}.dva-field label{font-size:11px;font-weight:900;letter-spacing:.1em;color:rgba(255,255,255,.58)}.dva-input,.dva-textarea,.dva-select{width:100%;border:1px solid rgba(255,255,255,.14);background:#0a0a0c;color:#fff;border-radius:12px;padding:12px;font:inherit}.dva-textarea{min-height:105px;resize:vertical}.dva-checks{display:flex;gap:16px;flex-wrap:wrap;grid-column:1/-1}.dva-checks label{display:flex;gap:8px;align-items:center;color:rgba(255,255,255,.75);font-size:13px}.dva-actions{display:flex;gap:10px;flex-wrap:wrap;margin-top:16px}.dva-btn{border:1px solid rgba(255,255,255,.16);background:rgba(255,255,255,.07);color:#fff;border-radius:999px;padding:11px 16px;font-weight:1000;cursor:pointer;text-decoration:none}.dva-btn--primary{background:linear-gradient(135deg,#e31b2c,#72030d);border-color:rgba(255,87,98,.7)}.dva-btn--green{background:linear-gradient(135deg,#168448,#07562c);border-color:rgba(87,230,142,.45)}.dva-btn:disabled{opacity:.45;cursor:not-allowed}.dva-alert{border-radius:14px;padding:13px 15px;margin-top:14px}.dva-alert--error{background:rgba(133,13,22,.4);border:1px solid rgba(255,77,90,.38);color:#ffc0c5}.dva-alert--ok{background:rgba(13,104,54,.34);border:1px solid rgba(79,224,139,.32);color:#c4f9d5}.dva-stat strong{display:block;font-size:2rem;margin-top:5px}.dva-ready{display:flex;justify-content:space-between;gap:12px;border-bottom:1px solid rgba(255,255,255,.08);padding:10px 0}.dva-ready span:last-child{font-weight:1000}.dva-good{color:#8bf0b1}.dva-bad{color:#ff9da5}.dva-warning{border-color:rgba(255,184,69,.35);background:linear-gradient(145deg,rgba(71,40,4,.65),rgba(8,8,10,.97))}.dva-credentials{font-family:ui-monospace,SFMono-Regular,Consolas,monospace;word-break:break-all;background:#050506;border:1px solid rgba(255,255,255,.12);border-radius:12px;padding:12px;margin-top:8px}.dva-table-wrap{overflow-x:auto}.dva-table{width:100%;border-collapse:collapse;min-width:760px}.dva-table th,.dva-table td{text-align:left;padding:10px 8px;border-bottom:1px solid rgba(255,255,255,.08);font-size:13px}.dva-table th{font-size:10px;letter-spacing:.1em;color:rgba(255,255,255,.5)}@media(max-width:850px){.dva-layout{grid-template-columns:1fr}.dva-form{grid-template-columns:1fr}.dva-field--wide{grid-column:auto}}
      `}</style>

      <section className="dva-hero">
        <div className="dva-eyebrow">FOUNDER-ONLY PRIVATE BUILD</div>
        <h1 className="dva-title">GAStreaming Digital Venue</h1>
        <p className="dva-copy">Build and test a ticketed exclusive event where the performer only approves the engagement, supports promotion, and performs. GAStreaming handles the venue, payment, secure access, production handoff, customer support, replay controls, and revenue ledger.</p>
        <div className="dva-actions"><Link className="dva-btn" to="/">GAStreaming home</Link>{previewUrl ? <button className="dva-btn" onClick={() => copy(previewUrl, "Private preview link")}>Copy private preview</button> : null}</div>
      </section>

      {notice ? <div className="dva-alert dva-alert--ok">{notice}</div> : null}
      {error ? <div className="dva-alert dva-alert--error">{error}</div> : null}
      {loading ? <section className="dva-card" style={{ marginTop: 18 }}>Loading the private control center…</section> : null}

      {!loading && readiness ? (
        <section className="dva-grid">
          <div className="dva-card dva-warning">
            <div className="dva-eyebrow">PUBLIC LAUNCH LOCK</div>
            <h2>{readiness.publicLaunchBlocked ? "Locked on purpose" : "Explicitly enabled"}</h2>
            <p className="dva-copy">The demo can be tested privately. Public sales remain blocked until you deliberately enable them after the readiness work.</p>
          </div>
          <div className="dva-card">
            <div className="dva-ready"><span>Stripe ticket checkout</span><span className={readiness.stripeConfigured ? "dva-good" : "dva-bad"}>{readiness.stripeConfigured ? "READY" : "MISSING"}</span></div>
            <div className="dva-ready"><span>Mux venue provisioning</span><span className={readiness.muxApiConfigured ? "dva-good" : "dva-bad"}>{readiness.muxApiConfigured ? "READY" : "MISSING"}</span></div>
            <div className="dva-ready"><span>Mux signed playback</span><span className={readiness.muxSigningConfigured ? "dva-good" : "dva-bad"}>{readiness.muxSigningConfigured ? "READY" : "MISSING"}</span></div>
            <div className="dva-ready"><span>Production database</span><span className="dva-bad">REQUIRED BEFORE PUBLIC</span></div>
          </div>
        </section>
      ) : null}

      {!loading ? (
        <section className="dva-layout">
          <aside className="dva-card">
            <div className="dva-eyebrow">PRIVATE EVENTS</div>
            <div className="dva-list" style={{ marginTop: 12 }}>
              {events.map((event) => (
                <button key={event.id} className={`dva-event-btn ${form.id === event.id ? "dva-event-btn--active" : ""}`} onClick={() => { setForm(formFromEvent(event)); setProduction(null); }}>
                  <strong>{event.title}</strong><br /><small>{event.status.replaceAll("_", " ")} · {money(event.ticketPriceCents)}</small>
                </button>
              ))}
              <button className="dva-event-btn" onClick={() => { setForm({ ...EMPTY_FORM, startsAt: defaultStart() }); setProduction(null); }}>+ New private demo event</button>
            </div>
          </aside>

          <div>
            <section className="dva-card">
              <div className="dva-eyebrow">EVENT CONFIGURATION</div>
              <h2>{form.id ? "Edit the private event" : "Create the private mock event"}</h2>
              <div className="dva-form">
                <div className="dva-field"><label>EVENT TITLE</label><input className="dva-input" value={form.title} onChange={(e) => update("title", e.target.value)} /></div>
                <div className="dva-field"><label>URL SLUG</label><input className="dva-input" value={form.slug} onChange={(e) => update("slug", e.target.value)} /></div>
                <div className="dva-field"><label>PERFORMER / HEADLINER</label><input className="dva-input" placeholder="Leave blank for the mock test" value={form.performerName} onChange={(e) => update("performerName", e.target.value)} /></div>
                <div className="dva-field"><label>EVENT DATE & TIME</label><input className="dva-input" type="datetime-local" value={form.startsAt} onChange={(e) => update("startsAt", e.target.value)} /></div>
                <div className="dva-field dva-field--wide"><label>SUBTITLE</label><input className="dva-input" value={form.subtitle} onChange={(e) => update("subtitle", e.target.value)} /></div>
                <div className="dva-field dva-field--wide"><label>DESCRIPTION</label><textarea className="dva-textarea" value={form.description} onChange={(e) => update("description", e.target.value)} /></div>
                <div className="dva-field"><label>TICKET PRICE (CENTS)</label><input className="dva-input" type="number" min="50" value={form.ticketPriceCents} onChange={(e) => update("ticketPriceCents", e.target.value)} /></div>
                <div className="dva-field"><label>CAPACITY</label><input className="dva-input" type="number" min="1" value={form.capacity} onChange={(e) => update("capacity", e.target.value)} /></div>
                <div className="dva-field"><label>DURATION (MINUTES)</label><input className="dva-input" type="number" value={form.durationMinutes} onChange={(e) => update("durationMinutes", e.target.value)} /></div>
                <div className="dva-field"><label>DOORS OPEN BEFORE (MINUTES)</label><input className="dva-input" type="number" value={form.doorsOpenMinutes} onChange={(e) => update("doorsOpenMinutes", e.target.value)} /></div>
                <div className="dva-field"><label>PERFORMER SHARE (BASIS POINTS)</label><input className="dva-input" type="number" min="0" max="10000" value={form.performerBps} onChange={(e) => update("performerBps", e.target.value)} /><small>{Number(form.performerBps || 0) / 100}% performer / {(10000 - Number(form.performerBps || 0)) / 100}% GAStreaming</small></div>
                <div className="dva-field"><label>STATUS</label><select className="dva-select" value={form.status} onChange={(e) => update("status", e.target.value)}><option value="draft">Draft</option><option value="private_test">Private test</option><option value="ended">Ended</option><option value="cancelled">Cancelled</option><option value="on_sale" disabled={readiness?.publicLaunchBlocked}>On sale</option><option value="doors_open" disabled={readiness?.publicLaunchBlocked}>Doors open</option><option value="live" disabled={readiness?.publicLaunchBlocked}>Live</option><option value="replay" disabled={readiness?.publicLaunchBlocked}>Replay</option></select></div>
                <div className="dva-field dva-field--wide"><label>ARTWORK URL</label><input className="dva-input" value={form.artworkUrl} onChange={(e) => update("artworkUrl", e.target.value)} /></div>
                <div className="dva-field dva-field--wide"><label>REFUND POLICY</label><textarea className="dva-textarea" value={form.refundPolicy} onChange={(e) => update("refundPolicy", e.target.value)} /></div>
                <div className="dva-checks">
                  <label><input type="checkbox" checked={form.ticketSalesOpen} onChange={(e) => update("ticketSalesOpen", e.target.checked)} /> Private ticket test enabled</label>
                  <label><input type="checkbox" checked={form.testPlaybackEnabled} onChange={(e) => update("testPlaybackEnabled", e.target.checked)} /> Private playback test enabled</label>
                  <label><input type="checkbox" checked={form.allowReplay} onChange={(e) => update("allowReplay", e.target.checked)} /> Replay permitted</label>
                  <label><input type="checkbox" checked={form.chatEnabled} onChange={(e) => update("chatEnabled", e.target.checked)} /> Event chat</label>
                </div>
                {form.allowReplay ? <div className="dva-field"><label>REPLAY ENDS</label><input className="dva-input" type="datetime-local" value={form.replayEndsAt} onChange={(e) => update("replayEndsAt", e.target.value)} /></div> : null}
              </div>
              <div className="dva-actions"><button className="dva-btn dva-btn--primary" disabled={busy === "save"} onClick={() => save()}>{busy === "save" ? "Saving…" : "Save private event"}</button><button className="dva-btn" onClick={() => setAdvanced((value) => !value)}>{advanced ? "Hide" : "Show"} secure playback fields</button></div>
            </section>

            {advanced ? (
              <section className="dva-card" style={{ marginTop: 18 }}>
                <div className="dva-eyebrow">SECURE PLAYBACK — FOUNDER ONLY</div>
                <p className="dva-copy">Paid events must use signed Mux playback IDs. Public playback IDs are rejected by the access API.</p>
                <div className="dva-form">
                  <div className="dva-field dva-field--wide"><label>LIVE SIGNED PLAYBACK ID</label><input className="dva-input" value={form.livePlaybackId} onChange={(e) => update("livePlaybackId", e.target.value)} /></div>
                  <div className="dva-field dva-field--wide"><label>REPLAY SIGNED PLAYBACK ID</label><input className="dva-input" value={form.replayPlaybackId} onChange={(e) => update("replayPlaybackId", e.target.value)} /></div>
                  <div className="dva-field dva-field--wide"><label>PLAYBACK RESTRICTION ID (OPTIONAL)</label><input className="dva-input" value={form.playbackRestrictionId} onChange={(e) => update("playbackRestrictionId", e.target.value)} /></div>
                </div>
              </section>
            ) : null}

            {form.id ? (
              <section className="dva-card" style={{ marginTop: 18 }}>
                <div className="dva-eyebrow">PRODUCTION HANDOFF</div>
                <h2>The performer does not touch OBS.</h2>
                <p className="dva-copy">GAStreaming or the performer's existing production person receives the RTMP credentials. The performer only needs a camera-ready location, microphone, and performance time.</p>
                {!current?.stream?.liveStreamId ? <button className="dva-btn dva-btn--green" disabled={busy === "mux" || !readiness?.muxApiConfigured} onClick={provisionMux}>{busy === "mux" ? "Creating private venue…" : "Provision private signed Mux venue"}</button> : <span className="dva-good"><strong>PRIVATE MUX VENUE PROVISIONED</strong></span>}
                {production ? <div><div className="dva-credentials">Server: {production.rtmpServer}</div><div className="dva-credentials">Stream key: {production.streamKey || "Not returned"}</div><div className="dva-actions"><button className="dva-btn" onClick={() => copy(`${production.rtmpServer}\n${production.streamKey}`, "Production credentials")}>Copy production credentials</button></div></div> : null}
              </section>
            ) : null}

            {current ? (
              <>
                <section className="dva-grid">
                  <div className="dva-card dva-stat"><div className="dva-eyebrow">GROSS TICKETS</div><strong>{money(revenue.grossCents)}</strong></div>
                  <div className="dva-card dva-stat"><div className="dva-eyebrow">DEFINED NET</div><strong>{money(revenue.distributableNetCents)}</strong></div>
                  <div className="dva-card dva-stat"><div className="dva-eyebrow">PERFORMER 80%</div><strong>{money(revenue.performerShareCents)}</strong></div>
                  <div className="dva-card dva-stat"><div className="dva-eyebrow">GASTREAMING 20%</div><strong>{money(revenue.platformShareCents)}</strong></div>
                </section>
                <section className="dva-card" style={{ marginTop: 18 }}>
                  <div className="dva-eyebrow">TRANSPARENT EVENT LEDGER</div>
                  <p className="dva-copy">{revenue.netDefinition} Production costs are not silently deducted; they require separate written approval.</p>
                  <div className="dva-table-wrap"><table className="dva-table"><thead><tr><th>TICKET</th><th>BUYER</th><th>STATUS</th><th>GROSS</th><th>REFUND</th><th>STRIPE FEE</th></tr></thead><tbody>{(current.tickets || []).map((ticket) => <tr key={ticket.id}><td>{ticket.id.slice(0, 15)}…</td><td>{ticket.email}</td><td>{ticket.status}</td><td>{money(ticket.grossCents)}</td><td>{money(ticket.refundedCents)}</td><td>{ticket.processingFeeFinal ? money(ticket.processingFeeCents) : "pending"}</td></tr>)}</tbody></table></div>
                </section>
              </>
            ) : null}
          </div>
        </section>
      ) : null}
    </main>
  );
}
